import { z } from 'zod';

import { FormPermCheckResultSchema } from './perm-check-schema.js';
import { FeedbackFormFieldsInputSchema } from './field-schema.js';
import { DiscordLabelTextSchema, FormNameSchema } from './shared.js';

// Status enum -------------------------------------------------------------
//
// 'draft' is the only status a freshly-created form holds. 'enabled' means
// the bot has posted (or is about to post) a button-message into the
// survey channel; 'disabled' means a previously enabled form has been
// taken down but kept around for re-enabling and for response history.
export const FeedbackFormStatusSchema = z.enum(['draft', 'enabled', 'disabled']);
export type FeedbackFormStatus = z.infer<typeof FeedbackFormStatusSchema>;

// Repost frequency enum ---------------------------------------------------
//
// pg_cron + the form_repost_queue (PR 6) drive the actual cadence; this
// enum is the contract the portal builder writes and the scheduler reads.
export const FormRepostFrequencySchema = z.enum(['never', 'daily', 'weekly', 'monthly']);
export type FormRepostFrequency = z.infer<typeof FormRepostFrequencySchema>;

// Name of the draft form auto-seeded by seed_default_feedback_form
// (migration 0044) the first time a game lands a Discord connection.
// Shared with the portal so the FormsSection banner can detect "this is
// the seeded starter" without hardcoding the string in two places. The
// SQL idempotency predicate also keys off the same exact string.
export const STARTER_FORM_NAME = 'Net Promoter Starter Form';

// Form (camelCase, portal-side) -------------------------------------------

export const FeedbackFormSchema = z.object({
  id: z.string().uuid(),
  gameId: z.string().uuid(),
  studioId: z.string().uuid(),
  // Portal-facing label. Shown in the forms list, kebab actions, and
  // inbox cards; the studio's working name for the form.
  name: FormNameSchema,
  // Portal-only notes - never shown to players.
  internalDescription: z.string().nullable(),
  // Discord-facing modal title. 45-char cap matches Discord's Label
  // component limit (the modal builder previously truncated long
  // titles with an ellipsis).
  title: DiscordLabelTextSchema,
  // Discord-facing copy under the modal title and on the posted
  // button-message.
  discordDescription: z.string().nullable(),
  status: FeedbackFormStatusSchema,
  surveyChannelId: z.string().nullable(),
  responseChannelId: z.string().nullable(),
  repostFrequency: FormRepostFrequencySchema,
  anonymous: z.boolean(),
  submitResponseTitle: z.string().nullable(),
  submitResponseDescription: z.string().nullable(),
  // Post-submit recruit opt-in (Story 10B, migration 0042). The bot
  // surfaces an "Add me to playtest channels" button on the ephemeral
  // thank-you reply iff at least one of these is non-empty.
  recruitChannelIds: z.array(z.string()).default([]),
  recruitRoleIds: z.array(z.string()).default([]),
  postedMessageId: z.string().nullable(),
  lastPostedAt: z.string().datetime({ offset: true }).nullable(),
  lastRepostedAt: z.string().datetime({ offset: true }).nullable(),
  enabledAt: z.string().datetime({ offset: true }).nullable(),
  disabledAt: z.string().datetime({ offset: true }).nullable(),
  archivedAt: z.string().datetime({ offset: true }).nullable(),
  lastPermCheckAt: z.string().datetime({ offset: true }).nullable(),
  lastPermCheckResult: FormPermCheckResultSchema.nullable(),
  createdByUserId: z.string().uuid().nullable(),
  createdAt: z.string().datetime({ offset: true }),
  updatedAt: z.string().datetime({ offset: true }),
});
export type FeedbackForm = z.infer<typeof FeedbackFormSchema>;

// Form (snake_case, DB row) -----------------------------------------------
//
// Used by the supabase-js `.from('feedback_forms').select(...)` read path.
// Transforms into the camelCase shape so callers downstream of the parse
// see a consistent surface.

export const FeedbackFormRowSchema = z
  .object({
    id: z.string().uuid(),
    game_id: z.string().uuid(),
    studio_id: z.string().uuid(),
    name: z.string(),
    internal_description: z.string().nullable(),
    title: z.string(),
    discord_description: z.string().nullable(),
    status: FeedbackFormStatusSchema,
    survey_channel_id: z.string().nullable(),
    response_channel_id: z.string().nullable(),
    repost_frequency: FormRepostFrequencySchema,
    anonymous: z.boolean(),
    submit_response_title: z.string().nullable(),
    submit_response_description: z.string().nullable(),
    recruit_channel_ids: z.array(z.string()).default([]),
    recruit_role_ids: z.array(z.string()).default([]),
    posted_message_id: z.string().nullable(),
    last_posted_at: z.string().datetime({ offset: true }).nullable(),
    last_reposted_at: z.string().datetime({ offset: true }).nullable(),
    enabled_at: z.string().datetime({ offset: true }).nullable(),
    disabled_at: z.string().datetime({ offset: true }).nullable(),
    archived_at: z.string().datetime({ offset: true }).nullable(),
    last_perm_check_at: z.string().datetime({ offset: true }).nullable(),
    last_perm_check_result: FormPermCheckResultSchema.nullable(),
    created_by_user_id: z.string().uuid().nullable(),
    created_at: z.string().datetime({ offset: true }),
    updated_at: z.string().datetime({ offset: true }),
  })
  .transform((row) => ({
    id: row.id,
    gameId: row.game_id,
    studioId: row.studio_id,
    name: row.name,
    internalDescription: row.internal_description,
    title: row.title,
    discordDescription: row.discord_description,
    status: row.status,
    surveyChannelId: row.survey_channel_id,
    responseChannelId: row.response_channel_id,
    repostFrequency: row.repost_frequency,
    anonymous: row.anonymous,
    submitResponseTitle: row.submit_response_title,
    submitResponseDescription: row.submit_response_description,
    recruitChannelIds: row.recruit_channel_ids,
    recruitRoleIds: row.recruit_role_ids,
    postedMessageId: row.posted_message_id,
    lastPostedAt: row.last_posted_at,
    lastRepostedAt: row.last_reposted_at,
    enabledAt: row.enabled_at,
    disabledAt: row.disabled_at,
    archivedAt: row.archived_at,
    lastPermCheckAt: row.last_perm_check_at,
    lastPermCheckResult: row.last_perm_check_result,
    createdByUserId: row.created_by_user_id,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  })) satisfies z.ZodType<FeedbackForm, z.ZodTypeDef, unknown>;

// RPC argument schemas ----------------------------------------------------
//
// Used by the portal-side data layer (apps/portal/src/games/feedback-
// forms-data.ts in PR 3) so a malformed call fails before round-tripping
// to Postgres.

export const CreateFeedbackFormArgsSchema = z.object({
  gameId: z.string().uuid(),
  // Portal label seed. The Discord title defaults to the first 45
  // chars of name and is refined separately via update_feedback_form.
  name: FormNameSchema,
});
export type CreateFeedbackFormArgs = z.infer<typeof CreateFeedbackFormArgsSchema>;

export const UpdateFeedbackFormArgsSchema = z.object({
  formId: z.string().uuid(),
  // Portal label (required) + portal-only notes (optional).
  name: FormNameSchema,
  internalDescription: z.string().nullable(),
  // Discord modal title (capped at 45 to match Discord's Label limit)
  // + the optional copy underneath / on the posted button-message.
  title: DiscordLabelTextSchema,
  discordDescription: z.string().nullable(),
  surveyChannelId: z.string().nullable(),
  responseChannelId: z.string().nullable(),
  repostFrequency: FormRepostFrequencySchema,
  anonymous: z.boolean(),
  submitResponseTitle: z.string().nullable(),
  submitResponseDescription: z.string().nullable(),
});
export type UpdateFeedbackFormArgs = z.infer<typeof UpdateFeedbackFormArgsSchema>;

export const ReplaceFeedbackFormFieldsArgsSchema = z.object({
  formId: z.string().uuid(),
  fields: FeedbackFormFieldsInputSchema,
});
export type ReplaceFeedbackFormFieldsArgs = z.infer<typeof ReplaceFeedbackFormFieldsArgsSchema>;

export const DeleteFeedbackFormArgsSchema = z.object({
  formId: z.string().uuid(),
});
export type DeleteFeedbackFormArgs = z.infer<typeof DeleteFeedbackFormArgsSchema>;
